import { usePostPOSBillOut } from '@api/pos/pos-transaction-api.rtk';
import { AmericanExpressIcon } from '@assets/icons/cash-breakdown/bank/AmericanExpressIcon';
import { DiscoverIcon } from '@assets/icons/cash-breakdown/bank/DiscoverIcon';
import { HSBCIcon } from '@assets/icons/cash-breakdown/bank/HSBCIcon';
import { JCBIcon } from '@assets/icons/cash-breakdown/bank/JCBICon';
import { MasterCardIcon } from '@assets/icons/cash-breakdown/bank/MasterCardIcon';
import { VisaCardIcon } from '@assets/icons/cash-breakdown/bank/VisaIcon';
import { ApplePayIcon } from '@assets/icons/cash-breakdown/epayment/ApplePayIcon';
import { GCashIcon } from '@assets/icons/cash-breakdown/epayment/GCashIcon';
import { GooglePayIcon } from '@assets/icons/cash-breakdown/epayment/GooglePayIcon';
import { MayaIcon } from '@assets/icons/cash-breakdown/epayment/MayaIcon';
import { PayPalIcon } from '@assets/icons/cash-breakdown/epayment/PayPalIcon';
import AutoCompleteController from '@components/form/controller/autocomplete.controller';
import DatePickerController from '@components/form/controller/date-picker.controller';
import InputFieldsController from '@components/form/controller/input-fields.controller';
import CustomNumberFormat from '@components/form/custom-number-format';
import { yupResolver } from '@hookform/resolvers/yup';
import useDisclosure, { UseDisclosureInterface } from '@hooks/useDisclosure';
import { ModalWrapper } from '@hooks/useModal';
import useToast from '@hooks/useToast';
import { optionsBank } from '@modules/front-office/components/room-posting/fo-roomposting-popover';
import {
  selectPaymentDetailsByTransaction,
  selectPaymentDetailsCountByTransaction,
  selectPOSEntriesMasterlist,
  selectPOSPrinter,
  selectPOSShiftID,
  selectPOSstationID,
  selectPOSTableOrders,
  selectPOSTableOrdersTotal,
  selectPOSTableTransactionID,
} from '@modules/point-of-sale/slice/pos-selector';
import {
  clearPaymentDetails,
  deletePaymentDetail,
  removeSelectedTable,
  setCardPaymentDetails,
  setEPaymentPaymentDetails,
  setOthersPaymentDetails,
} from '@modules/point-of-sale/slice/pos-table-area.slice';
import {
  ArrowBack,
  ArrowForward,
  CheckOutlined,
  MailOutline,
  PaymentsOutlined,
} from '@mui/icons-material';
import { LoadingButton } from '@mui/lab';
import {
  Badge,
  Button,
  Collapse,
  Divider,
  IconButton,
  InputAdornment,
  Stack,
  SvgIcon,
  SvgIconTypeMap,
  Typography,
} from '@mui/material';
import { OverridableComponent } from '@mui/material/OverridableComponent';
import Grid2 from '@mui/material/Unstable_Grid2';
import { useAppDispatch, useAppSelector } from '@store/index';
import { selectPropertyID } from '@store/selector/property.selector';
import { IconCreditCard, IconDevicesDollar } from '@tabler/icons-react';
import {
  formatNumber,
  parseNumber,
  payloadDateFormat,
  regex,
} from '@utils/format';
import { hashSignature } from '@utils/security';
import moment, { Moment } from 'moment';
import { useMemo, useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import * as yup from 'yup';
import POSSplitBillModal from './others/pos-split-bill.modal';
import POSOrderTakingWindow, {
  usePOSWindowDrawerCtx,
} from './pos-order-taking-window';

// #region INTERFACE
type TPayments = 'credit-debit' | 'e-payment' | 'others' | 'receipt';

interface IPOSBillOutForm {
  amountReceived: string;
  amount: string;

  // Card
  cardDigit?: string;
  approvalNumber?: string;
  cardHolderName?: string;
  expirationDate?: string;
  email?: string;

  // E-Payment
  refNo?: string;
  refInvNo?: string;
  remarks?: string;

  // Others
  issuingBank?: string;
  checkDate?: Moment;
  checkNo?: string;
  giftCertificate?: string;
}

const defaultValues: IPOSBillOutForm = {
  cardDigit: '',
  approvalNumber: '',
  cardHolderName: '',
  amount: '',
  remarks: '',
  email: '',
  expirationDate: '',
  refNo: '',
  refInvNo: '',
  issuingBank: '',
  checkDate: moment(),
  checkNo: '',
  giftCertificate: '',
  amountReceived: '0',
};

const formSchema = (payment?: TPayments, selectedCard?: 'Cheque' | 'GC') => {
  const baseSchema = yup.object().shape({
    amount: yup.string().default('').required(),
  });

  const cardSchema = yup.object().shape({
    cardDigit: yup.string().default('').required(),
    approvalNumber: yup.string().default('').required(),
    cardHolderName: yup.string().default('').required(),
    expirationDate: yup.string().default('').required(),
  });

  const ePaymentSchema = yup.object().shape({
    remarks: yup.string().default('').notRequired(),
    refInvNo: yup.string().default('').notRequired(),
    refNo: yup.string().default('').required(),
  });

  const othersSchema = yup.object().shape({
    remarks: yup.string().default('').required(),
  });

  const emailSchema = yup.object().shape({
    email: yup.string().default('').required(),
  });

  const chequeSchema = yup.object().shape({
    issuingBank: yup.string().default('').required(),
    checkDate: yup.string().default('').required(),
    checkNo: yup.string().default('').required(),
  });

  const gcSchema = yup.object().shape({
    giftCertificate: yup.string().default('').required(),
  });

  if (payment === 'credit-debit') {
    return baseSchema.concat(
      cardSchema,
    ) as unknown as yup.ObjectSchema<IPOSBillOutForm>;
  } else if (payment === 'e-payment') {
    return baseSchema.concat(
      ePaymentSchema,
    ) as unknown as yup.ObjectSchema<IPOSBillOutForm>;
  } else if (payment === 'others') {
    let schema = baseSchema.concat(othersSchema);

    if (selectedCard === 'Cheque') {
      schema = schema.concat(chequeSchema);
    }

    if (selectedCard === 'GC') {
      schema = schema.concat(gcSchema);
    }

    return schema as unknown as yup.ObjectSchema<IPOSBillOutForm>;
  } else if (payment === 'receipt') {
    return baseSchema.concat(
      emailSchema,
    ) as unknown as yup.ObjectSchema<IPOSBillOutForm>;
  }

  return baseSchema as yup.ObjectSchema<IPOSBillOutForm>;
};

interface IPOSBillOutButtons {
  referenceID: string;
  cardType: string;
  name: string;
  onClick: () => void | Promise<void>;
  title: string;
  icon?: OverridableComponent<SvgIconTypeMap<{}, 'svg'>> & {
    muiName: string;
  };
  disabled?: boolean;
}

interface ISelectedCard extends Omit<IPOSBillOutButtons, 'onClick'> {}

interface IIconPicker extends Pick<IPOSBillOutButtons, 'icon'> {
  title: string[];
}
// #endregion

// #region COMPONENT
const icons: IIconPicker[] = [
  // CARD
  {
    title: ['Mastercard', 'Master Card'],
    icon: MasterCardIcon,
  },
  {
    title: ['Visa'],
    icon: VisaCardIcon,
  },
  {
    title: ['JCB'],
    icon: JCBIcon,
  },
  {
    title: ['HSBC'],
    icon: HSBCIcon,
  },
  {
    title: ['Discover'],
    icon: DiscoverIcon,
  },
  {
    title: ['American Express', 'AmEx'],
    icon: AmericanExpressIcon,
  },

  // E-PAYMENT
  {
    title: ['Paypal'],
    icon: PayPalIcon,
  },
  {
    title: ['GCash'],
    icon: GCashIcon,
  },
  {
    title: ['Maya'],
    icon: MayaIcon,
  },
  {
    title: ['Apple'],
    icon: ApplePayIcon,
  },
  {
    title: ['Google'],
    icon: GooglePayIcon,
  },
];
const getMatchedIcon = (description: string, icons: IIconPicker[]) => {
  const lowerDesc = description.toLowerCase();
  return icons.find((icon) =>
    icon.title.some((t) => lowerDesc.includes(t.toLowerCase())),
  );
};

const CardItem = ({
  button,
  selectedCard,
  totalCard,
  badgeCount,
  type,
}: {
  button: IPOSBillOutButtons;
  selectedCard?: ISelectedCard;
  totalCard: number;
  badgeCount?: number;
  type: 'card' | 'ePayment' | 'others';
}) => {
  const isSelected = button.title === selectedCard?.title;
  const [hover, setHover] = useState(false);
  const { toast } = useToast();
  const transactionID = useAppSelector(selectPOSTableTransactionID);
  const dispatch = useAppDispatch();

  return (
    <Grid2 {...{ xs: 12, sm: 6, md: 12 / (totalCard <= 5 ? 5 : totalCard) }}>
      <Badge
        badgeContent={
          hover
            ? `Remove item${badgeCount && badgeCount > 1 ? 's' : ''}`
            : badgeCount
        }
        color={hover ? 'error' : 'primary'}
        component={Stack}
        slotProps={{
          badge: {
            onMouseEnter: () => setHover(true),
            onMouseLeave: () => setHover(false),
            style: {
              cursor: 'pointer',
              color: '#FFF',
            },
            onClick: () => {
              dispatch(
                deletePaymentDetail({
                  transactionID,
                  type,
                  referenceID: button.referenceID,
                }),
              );
              toast({
                message: 'Payment removed successfully',
                severity: 'success',
              });
            },
          },
        }}
        sx={{ width: '100%' }}
      >
        <Stack
          gap={2}
          sx={{
            width: '100%',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '130px',
            maxWidth: '150px',
            cursor: button.disabled ? 'not-allowed' : 'pointer',
            borderRadius: '12px',
            border: '1px solid',
            borderColor: isSelected ? 'primary.main' : '#EEE',
            bgcolor: button.disabled
              ? '#F5F5F5'
              : isSelected
                ? 'primary.200'
                : '#FFF',
            opacity: button.disabled ? 0.5 : 1,
            ':hover': button.disabled
              ? {}
              : {
                  bgcolor: 'primary.100',
                },
          }}
          onClick={button.disabled ? undefined : button.onClick}
        >
          {button.icon && (
            <SvgIcon
              component={button.icon}
              sx={{
                fontSize: '52px',
                color: 'transparent',
              }}
            />
          )}
          <Typography
            sx={{
              fontWeight: 400,
              color: '#212B36',
              textAlign: 'center',
            }}
          >
            {button.title}
          </Typography>
        </Stack>
      </Badge>
    </Grid2>
  );
};
// #endregion

const POSBillOutModal = ({
  modalDisclosure,
}: {
  modalDisclosure: UseDisclosureInterface;
}) => {
  // #region STATE
  const {
    tableDrawerDisclosure: { onOpen },
  } = usePOSWindowDrawerCtx();
  const { toast } = useToast();
  const dispatch = useAppDispatch();

  const splitBillModal = useDisclosure();
  const [showOrders, setShowOrders] = useState(true);
  const [paymentType, setPaymentType] = useState<TPayments>();
  const [selectedCard, setSelectedCard] = useState<ISelectedCard>();

  const otherCard = useMemo(() => {
    if (['Cheque', 'Check'].includes(selectedCard?.title ?? ''))
      return 'Cheque';

    if (
      [
        'Vouchers',
        'Voucher',
        'Vouch',
        'GC',
        'Gift Cert',
        'Gift Certificates',
        'Gift Certificate',
      ].includes(selectedCard?.title ?? '')
    )
      return 'GC';
  }, [selectedCard]);

  const orders = useAppSelector(selectPOSTableOrders);
  const totalCost = useAppSelector(selectPOSTableOrdersTotal);
  const transactionID = useAppSelector(selectPOSTableTransactionID);
  const propertyID = useAppSelector(selectPropertyID);
  const stationID = useAppSelector(selectPOSstationID);
  const shiftID = useAppSelector(selectPOSShiftID);
  const paymentDetails = useAppSelector(selectPaymentDetailsByTransaction);
  const printerID = useAppSelector(selectPOSPrinter);
  const {
    card,
    ePayment,
    others,
    referenceCount: {
      card: cardItems,
      ePayment: ePaymentItems,
      others: othersItems,
    },
    amount: {
      total,
      card: totalCardAmount,
      ePayment: totalEPaymentAmount,
      others: totalOthersAmount,
    },
  } = useAppSelector(selectPaymentDetailsCountByTransaction);

  const filteredData = useAppSelector(selectPOSEntriesMasterlist);

  const { remainingCost, isFullyPaid } = useMemo(() => {
    const remainingCost = Math.max(totalCost - total, 0);
    const overpaidAmount = Math.max(total - totalCost, 0);
    const isFullyPaid = total >= totalCost;

    return { remainingCost, isFullyPaid, overpaidAmount };
  }, [orders, total, totalCost]);
  // #endregion

  // #region API
  const [postBillOut, { isSuccess, data, isLoading }] = usePostPOSBillOut();
  // #endregion

  // #region FORMS / FUNCTIONS
  const {
    control,
    formState: { errors },
    handleSubmit,
    clearErrors,
    reset,
    setValue,
    watch,
    getValues,
  } = useForm<IPOSBillOutForm>({
    defaultValues,
    resolver: yupResolver<IPOSBillOutForm>(
      formSchema(isSuccess ? 'receipt' : paymentType, otherCard),
    ),
  });

  const onSubmit: SubmitHandler<IPOSBillOutForm> = async (data) => {
    try {
      if (!selectedCard) {
        toast({
          message: `${
            paymentType === 'e-payment'
              ? 'Select E-payment type to proceed'
              : paymentType === 'credit-debit'
                ? 'Select card type to proceed'
                : 'Select other type to proceed'
          }`,
          severity: 'warning',
        });
        return;
      }

      if (isFullyPaid) {
        toast({
          message:
            'Total amount due has been reached. Please proceed to charge.',
          severity: 'warning',
        });
        return;
      }

      if (paymentType === 'credit-debit') {
        dispatch(
          setCardPaymentDetails({
            transactionID,
            details: {
              amount: data.amount,
              approvalNumber: data.approvalNumber!,
              cardDigit: data.cardDigit!,
              cardHolderName: data.cardHolderName!,
              referenceID: selectedCard.referenceID,
              cardType: selectedCard.cardType,
              name: selectedCard.name,
              expirationDate: data.expirationDate!,
            },
          }),
        );
      }

      if (paymentType === 'e-payment') {
        dispatch(
          setEPaymentPaymentDetails({
            transactionID,
            details: {
              amount: data.amount,
              remarks: data.remarks!,
              referenceID: selectedCard.referenceID,
              cardType: selectedCard.cardType,
              name: selectedCard.name,
              refNo: data.refNo!,
              refInvNo: data.refInvNo,
            },
          }),
        );
      }

      if (paymentType === 'others') {
        dispatch(
          setOthersPaymentDetails({
            transactionID,
            details: {
              amount: data.amount,
              remarks: data.remarks!,
              referenceID: selectedCard.referenceID,
              cardType: selectedCard.cardType,
              name: selectedCard.name,

              ...(otherCard === 'GC' && {
                giftCertificate: data.giftCertificate!,
              }),
              ...(otherCard === 'Cheque' && {
                issuingBank: data.issuingBank!,
                checkDate: data.checkDate!,
                checkNo: data.checkNo!,
              }),
            },
          }),
        );
      }

      // if (paymentType === 'receipt') {
      // }

      if (
        paymentType === 'credit-debit' ||
        paymentType === 'e-payment' ||
        paymentType === 'others'
      ) {
        toast({
          message: 'Payment posted successfully',
          severity: 'success',
        });
      }

      const amountReceived = getValues('amountReceived');
      reset({ ...defaultValues, amountReceived });
      setTimeout(() => {
        clearErrors();
      }, 100);
    } catch (error) {
      toast({ message: error, severity: 'warning' });
    }
  };

  const handleAddCash = (cash: number) => {
    const currentAmount = getValues('amountReceived');
    setValue(
      'amountReceived',
      (parseFloat(currentAmount || '0') + cash).toString(),
    );
  };

  const onClickPayment = (item: TPayments) => {
    setPaymentType(item === paymentType ? undefined : item);
    onClickCard();
    clearErrors?.();
    const amountReceived = getValues('amountReceived');
    reset?.({ ...defaultValues, amountReceived });
  };

  const onClickCard = (card?: ISelectedCard) => {
    setSelectedCard(
      card?.referenceID === selectedCard?.referenceID ? undefined : card,
    );
    setTimeout(() => {
      clearErrors();
    }, 100);
  };

  const createBillOutButtons = (
    groupKey: keyof typeof filteredData,
    onClick: (item: Omit<IPOSBillOutButtons, 'onClick'>) => void,
    icons: IIconPicker[],
  ): IPOSBillOutButtons[] => {
    return (
      filteredData?.[groupKey]?.items?.map((item) => {
        const matchedIcon = getMatchedIcon(item.description, icons);

        const baseItem = {
          referenceID: item.referenceID,
          title: item.description,
          icon: matchedIcon?.icon,
          cardType: item.entryType,
          name: item.description,
        };

        return {
          ...baseItem,
          onClick: () => onClick(baseItem),
        };
      }) ?? []
    );
  };

  const creditDebitButtons = useMemo(
    () => createBillOutButtons('Card', onClickCard, icons),
    [filteredData?.['Card'], icons],
  );

  const ePaymentButtons = useMemo(
    () => createBillOutButtons('E-Payment', onClickCard, icons),
    [filteredData?.['E-Payment'], icons],
  );

  const othersButtons = useMemo(
    () => createBillOutButtons('Others', onClickCard, icons),
    [filteredData?.['Others'], icons],
  );

  const clearState = () => {
    dispatch(clearPaymentDetails({ transactionID }));
    dispatch(removeSelectedTable());
  };

  const handleCharge = async () => {
    try {
      const entry = filteredData?.['Cash']?.items?.find(
        (item) => item.referenceID,
      )?.referenceID;

      if (!entry) {
        toast({ message: 'No cash entry found', severity: 'warning' });
        return;
      }
      const currentAmount = getValues('amountReceived');

      const cashEntries = [
        {
          referenceID: entry,
          amount: parseNumber(currentAmount || '0'),
        },
      ];

      const cardEntries =
        paymentDetails?.['card']?.map((item) => ({
          referenceID: item.referenceID,
          amount: parseNumber(item.amount),
          paymentDetails: {
            or_no: '',
            card_type: item.cardType,
            card_number: item.cardDigit,
            card_network: item.name,
            approval_code: item.approvalNumber,
            cc_issuing_bank: item.name,
            expiration_date: item.expirationDate,
            card_holders_name: item.cardHolderName,
          },
        })) ?? [];

      const ePaymentEntries =
        paymentDetails?.['ePayment']?.map((item) => ({
          referenceID: item.referenceID,
          amount: parseNumber(item.amount),
          paymentDetails: {
            ref_no: item.refNo!,
            ref_inv_no: item.refInvNo,
            remarks: item.remarks,
          },
        })) ?? [];

      const othersEntries =
        paymentDetails?.['others']?.map((item) => ({
          referenceID: item.referenceID,
          amount: parseNumber(item.amount),
          paymentDetails: {
            ...('giftCertificate' in item && {
              gift_certificate_number: item.giftCertificate!,
              remarks: item.remarks,
            }),

            ...('checkNo' in item && {
              check_date: moment(item.checkDate! ?? '').format(
                payloadDateFormat,
              ),
              check_number: item.checkNo!,
              issuing_bank: item.issuingBank!,
              remarks: item.remarks,
            }),
          },
        })) ?? [];

      const entries = [
        ...cashEntries,
        ...cardEntries,
        ...ePaymentEntries,
        ...othersEntries,
      ];

      const payload = {
        properties: [
          {
            property: {
              propertyID,
              shiftID,
              stationID,
              entries,
              signature: hashSignature(
                `${propertyID}${shiftID}${stationID}${entries.length}`,
              ),
            },
          },
        ],
      };

      const result = await postBillOut({
        transactionID,
        payload,
        printerRefID: printerID || '',
      }).unwrap();

      if (result?.parsedResponse.responseCode === 200) {
        toast({
          message: 'Bill-out completed successfully',
          severity: 'success',
        });
        handleModalClose(true);
      } else {
        toast({
          message: 'Failed to bill out the transaction',
          severity: 'error',
        });
      }
    } catch (error) {
      toast({ message: error, severity: 'warning' });

      const message =
        typeof error === 'object' && error !== null && 'errMsg' in error
          ? String((error as any).errMsg)
          : '';

      if (message?.toLowerCase()?.includes('transaction posted successfully')) {
        handleModalClose();
      }
    }
  };

  const handleNewSale = () => {
    clearState();
    onOpen();
  };

  const handleModalClose = (success?: boolean) => {
    if (typeof success === 'boolean' && success) clearState();

    modalDisclosure.onClose();
    setTimeout(() => {
      setShowOrders(true);
      setPaymentType(undefined);
      reset();
      clearErrors();
    }, 100);
  };
  // #endregion

  return (
    <>
      <ModalWrapper
        {...{
          modalDisclosure: { ...modalDisclosure, onClose: handleModalClose },
          withHeader: {
            title: 'Bill Out',
            onClose: handleModalClose,
          },
          boxStyle: {
            height: { xs: '100%' },
            width: { xs: '100%', sm: 'auto' },
          },
        }}
      >
        <Stack
          gap={2}
          direction={{ xs: 'column', sm: 'row' }}
          sx={{ flex: 1, minHeight: 0 }}
        >
          {/* ORDERS */}
          <Collapse in={showOrders} orientation='horizontal'>
            <POSOrderTakingWindow
              isBillOut={true}
              disabledLoading={isSuccess}
            />
          </Collapse>

          {/* BILL UI */}
          <Stack sx={{ flex: 1, minHeight: 0 }} gap={3}>
            {/* AMOUNT UI */}
            <Stack gap={1}>
              {/* SPLIT BUTTON */}
              <Stack direction='row' justifyContent='space-between'>
                <IconButton
                  onClick={() => setShowOrders(!showOrders)}
                  sx={{
                    bgcolor: '#ECECEC',
                    height: '48px',
                    width: '48px',
                    ':hover': { bgcolor: '#ECECEC' },
                  }}
                  disableRipple
                >
                  {showOrders ? <ArrowBack /> : <ArrowForward />}
                </IconButton>
                {!isSuccess &&
                  false && ( // force hide
                    <Button
                      variant='outlined'
                      size='large'
                      onClick={splitBillModal.onOpen}
                      disabled={isLoading}
                    >
                      Split
                    </Button>
                  )}
              </Stack>
              {!isSuccess && (
                <>
                  {/* TOTAL AMOUNT */}
                  <Stack sx={{ alignItems: 'center' }}>
                    <Typography
                      sx={{
                        fontFamily: 'Montserrat',
                        fontSize: '48px',
                        fontWeight: 600,
                        color: 'primary.main',
                        letterSpacing: '2px',
                      }}
                    >
                      {formatNumber(totalCost, 2, 2)}
                    </Typography>
                    <Typography sx={{ color: '#637381' }}>
                      Total amount due
                    </Typography>
                  </Stack>
                  {/* CASH RECEIVED */}
                  <Stack
                    direction={{ xs: 'column', md: 'row' }}
                    gap={3}
                    sx={{ alignItems: 'center' }}
                  >
                    <InputFieldsController
                      label='Amount Received'
                      control={control}
                      errors={errors}
                      name='amountReceived'
                      variant='standard'
                      InputProps={{
                        inputComponent: CustomNumberFormat,
                        inputProps: {
                          textAlign: 'left',
                          maxLength: 19,
                          'data-type': 'number-format',
                          minAmount: 1,
                        },
                        sx: {
                          '&:before': {
                            borderBottomColor: '#ddd',
                            borderWidth: 2,
                          },
                          '&:hover:not(.Mui-disabled):before': {
                            borderBottomColor: '#666',
                          },
                          '&:after': { borderBottomColor: 'primary.main' },
                          fontSize: '20px',
                        },

                        startAdornment: (
                          <InputAdornment position='start'>
                            <PaymentsOutlined sx={{ fontSize: 23 }} />
                          </InputAdornment>
                        ),
                      }}
                      InputLabelProps={{
                        sx: {
                          fontSize: '16px',
                        },
                      }}
                      placeholder='0.00'
                      borderErrorOnly
                    />

                    <Stack direction='row' gap={1.5}>
                      <Button
                        variant='outlined'
                        sx={{ color: 'error.main' }}
                        color='error'
                        disabled={
                          parseFloat(watch('amountReceived')) <= 0 || isLoading
                        }
                        onClick={() => setValue('amountReceived', '0')}
                        size='large'
                      >
                        Clear
                      </Button>
                      <LoadingButton
                        variant='contained'
                        sx={{ minWidth: '110px' }}
                        size='large'
                        onClick={handleCharge}
                        loading={isLoading}
                      >
                        Bill Out
                      </LoadingButton>
                    </Stack>
                  </Stack>
                </>
              )}
            </Stack>

            {/* BUTTONS UI */}
            <Stack
              component='form'
              onSubmit={handleSubmit(onSubmit)}
              sx={{
                maxWidth: '850px',
                flex: 1,
                minHeight: 0,
                overflowY: 'auto',
                p: 2,
              }}
              gap={3}
            >
              <Grid2
                container
                sx={{
                  ...(isSuccess && { height: '100%' }),
                  button: {
                    bgcolor: '#DFE3E8',
                    color: '#000',
                    width: '100%',
                    py: 2,
                    boxShadow: 1,
                  },
                }}
                spacing={2}
              >
                {!isSuccess ? (
                  <>
                    {/* ROW 1 */}
                    <Grid2 {...{ xs: 12, sm: 6, md: 3 }}>
                      <Button
                        variant='contained'
                        size='large'
                        onClick={() => handleAddCash(100)}
                      >
                        100
                      </Button>
                    </Grid2>
                    <Grid2 {...{ xs: 12, sm: 6, md: 3 }}>
                      <Button
                        variant='contained'
                        size='large'
                        onClick={() => handleAddCash(200)}
                      >
                        200
                      </Button>
                    </Grid2>
                    <Grid2 {...{ xs: 12, sm: 6, md: 3 }}>
                      <Button
                        variant='contained'
                        size='large'
                        onClick={() => handleAddCash(500)}
                      >
                        500
                      </Button>
                    </Grid2>
                    <Grid2 {...{ xs: 12, sm: 6, md: 3 }}>
                      <Button
                        variant='contained'
                        size='large'
                        onClick={() => handleAddCash(1000)}
                      >
                        1000
                      </Button>
                    </Grid2>

                    {/* ROW 2 */}
                    <Grid2 {...{ xs: 12 }}>
                      <Stack gap={3}>
                        <Badge
                          badgeContent={
                            paymentType === 'credit-debit' ? 0 : card
                          }
                          color='primary'
                        >
                          <Button
                            variant='contained'
                            size='large'
                            startIcon={<IconCreditCard />}
                            onClick={() => onClickPayment('credit-debit')}
                            sx={{ gap: 1 }}
                          >
                            Credit/Debit Card{' '}
                            {totalCardAmount > 0 && (
                              <span
                                style={{
                                  fontStyle: 'italic',
                                  fontWeight: 400,
                                  opacity: 0.6,
                                }}
                              >{`- Total Amount: ${totalCardAmount}`}</span>
                            )}
                          </Button>
                        </Badge>

                        {paymentType === 'credit-debit' && (
                          <>
                            <Stack px={1.2}>
                              <Grid2 container spacing={2}>
                                {creditDebitButtons.map((button, i) => (
                                  <CardItem
                                    key={button.title + i}
                                    badgeCount={cardItems?.[button.referenceID]}
                                    button={button}
                                    selectedCard={selectedCard}
                                    totalCard={creditDebitButtons.length}
                                    type='card'
                                  />
                                ))}
                              </Grid2>
                            </Stack>

                            <Stack px={1.2}>
                              <Grid2 container spacing={2}>
                                <Grid2 {...{ xs: 12, sm: 4 }}>
                                  <InputFieldsController
                                    variant='standard'
                                    control={control}
                                    errors={errors}
                                    name='cardDigit'
                                    label='Card Last 4 Digits'
                                    wrapperStyle={{ marginBottom: 0 }}
                                    size='medium'
                                    borderErrorOnly
                                    InputProps={{
                                      inputComponent: CustomNumberFormat,
                                      inputProps: {
                                        format: '####',
                                        placeholder: '####',
                                        mask: '_',
                                        textAlign: 'left',
                                        decimalScale: 0,
                                        thousandSeparator: false,
                                      },
                                    }}
                                  />
                                </Grid2>

                                <Grid2 {...{ xs: 12, sm: 4 }}>
                                  <InputFieldsController
                                    variant='standard'
                                    label='Expiration Date '
                                    name='expirationDate'
                                    control={control}
                                    errors={errors}
                                    InputProps={{
                                      inputComponent: CustomNumberFormat,
                                      inputProps: {
                                        format: '##/##',
                                        mask: '_',
                                        placeholder: '##/##',
                                        textAlign: 'left',
                                        decimalScale: 0,
                                        thousandSeparator: false,
                                        isAllowed: (values) => {
                                          const { formattedValue } = values;

                                          const [monthPart] =
                                            formattedValue.split('/');

                                          const month = monthPart.replace(
                                            /\D/g,
                                            '',
                                          );

                                          if (!month || month.length < 2)
                                            return true;

                                          const monthNum = Number(month);

                                          // Validate that month is between 1 and 12
                                          return (
                                            monthNum >= 1 && monthNum <= 12
                                          );
                                        },
                                      },
                                    }}
                                    wrapperStyle={{ marginBottom: 0 }}
                                    borderErrorOnly
                                  />
                                </Grid2>

                                <Grid2 {...{ xs: 12, sm: 4 }}>
                                  <InputFieldsController
                                    variant='standard'
                                    control={control}
                                    errors={errors}
                                    name='approvalNumber'
                                    label='Approval Code'
                                    wrapperStyle={{ marginBottom: 0 }}
                                    size='medium'
                                    borderErrorOnly
                                    InputProps={{
                                      // inputComponent: CustomNumberFormat,
                                      inputProps: {
                                        maxLength: 8,
                                        textAlign: 'left',
                                        decimalScale: 0,
                                        thousandSeparator: false,
                                      },
                                    }}
                                    onChange={(e) => {
                                      const value = e.target.value;

                                      const cleaned = value.replace(
                                        regex.lettersAndNumber,
                                        '',
                                      );
                                      e.target.value = cleaned;

                                      setValue('approvalNumber', cleaned);
                                    }}
                                  />
                                </Grid2>

                                <Grid2
                                  {...{ xs: 12, sm: 4.5 }}
                                  sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                  }}
                                >
                                  <InputFieldsController
                                    variant='standard'
                                    control={control}
                                    errors={errors}
                                    name='cardHolderName'
                                    label='Card Holder Name'
                                    wrapperStyle={{ marginBottom: 0 }}
                                    size='medium'
                                    borderErrorOnly
                                  />
                                </Grid2>

                                <Grid2
                                  {...{ xs: 4.5 }}
                                  sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                  }}
                                >
                                  <InputFieldsController
                                    variant='standard'
                                    control={control}
                                    errors={errors}
                                    name='amount'
                                    label='Amount'
                                    wrapperStyle={{ marginBottom: 0 }}
                                    size='medium'
                                    borderErrorOnly
                                    InputProps={{
                                      inputComponent: CustomNumberFormat,
                                      inputProps: {
                                        maxLength: 16,
                                        textAlign: 'left',
                                        maxAmount: remainingCost,
                                        allowNegative: false,
                                      },
                                    }}
                                  />
                                </Grid2>
                                <Grid2 {...{ xs: 1.5 }}>
                                  <Button
                                    variant='contained'
                                    sx={{
                                      // bgcolor: '#2B3493!important',
                                      // color: '#FFF!important',
                                      boxShadow: 'none!important',
                                    }}
                                    disabled
                                  >
                                    MSR
                                  </Button>
                                </Grid2>
                                <Grid2 {...{ xs: 1.5 }}>
                                  <Button
                                    variant='contained'
                                    sx={{
                                      bgcolor: '#2B3493!important',
                                      color: '#FFF!important',
                                      boxShadow: 'none!important',
                                    }}
                                    type='submit'
                                  >
                                    Post
                                  </Button>
                                </Grid2>
                              </Grid2>
                            </Stack>
                          </>
                        )}
                      </Stack>
                    </Grid2>

                    {/* ROW 3 */}
                    <Grid2 {...{ xs: 12 }}>
                      <Stack gap={3}>
                        <Badge
                          badgeContent={
                            paymentType === 'e-payment' ? 0 : ePayment
                          }
                          color='primary'
                        >
                          <Button
                            variant='contained'
                            size='large'
                            startIcon={<IconDevicesDollar />}
                            onClick={() => onClickPayment('e-payment')}
                            sx={{ gap: 1 }}
                          >
                            E-Payment{' '}
                            {totalEPaymentAmount > 0 && (
                              <span
                                style={{
                                  fontStyle: 'italic',
                                  fontWeight: 400,
                                  opacity: 0.6,
                                }}
                              >{`- Total Amount: ${totalEPaymentAmount}`}</span>
                            )}
                          </Button>
                        </Badge>
                        {paymentType === 'e-payment' && (
                          <>
                            <Stack px={1.2}>
                              <Grid2 container spacing={2}>
                                {ePaymentButtons.map((button, i) => (
                                  <CardItem
                                    key={button.title + i}
                                    button={button}
                                    badgeCount={
                                      ePaymentItems?.[button.referenceID]
                                    }
                                    selectedCard={selectedCard}
                                    totalCard={ePaymentButtons.length}
                                    type='ePayment'
                                  />
                                ))}
                              </Grid2>
                            </Stack>

                            <Stack px={1.2}>
                              <Grid2 container spacing={2}>
                                <Grid2 {...{ xs: 12, sm: 4 }}>
                                  <InputFieldsController
                                    variant='standard'
                                    control={control}
                                    errors={errors}
                                    name='amount'
                                    label='Amount'
                                    wrapperStyle={{ marginBottom: 0 }}
                                    size='medium'
                                    borderErrorOnly
                                    InputProps={{
                                      inputComponent: CustomNumberFormat,
                                      inputProps: {
                                        maxLength: 16,
                                        textAlign: 'left',
                                        maxAmount: remainingCost,
                                        allowNegative: false,
                                      },
                                    }}
                                  />
                                </Grid2>

                                <Grid2 {...{ xs: 12, sm: 4 }}>
                                  <InputFieldsController
                                    variant='standard'
                                    control={control}
                                    errors={errors}
                                    name='refNo'
                                    label='Reference No'
                                    wrapperStyle={{ marginBottom: 0 }}
                                    size='medium'
                                    borderErrorOnly
                                    InputProps={{
                                      inputProps: {
                                        maxLength: 16,
                                        textAlign: 'left',
                                      },
                                    }}
                                  />
                                </Grid2>

                                <Grid2 {...{ xs: 12, sm: 4 }}>
                                  <InputFieldsController
                                    variant='standard'
                                    control={control}
                                    errors={errors}
                                    name='refInvNo'
                                    label='Reference Invoice No'
                                    wrapperStyle={{ marginBottom: 0 }}
                                    size='medium'
                                    borderErrorOnly
                                    InputProps={{
                                      inputProps: {
                                        maxLength: 16,
                                        textAlign: 'left',
                                      },
                                    }}
                                  />
                                </Grid2>

                                <Grid2
                                  {...{ xs: 10.5 }}
                                  sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                  }}
                                >
                                  <InputFieldsController
                                    variant='standard'
                                    control={control}
                                    errors={errors}
                                    name='remarks'
                                    label='Remarks'
                                    wrapperStyle={{ marginBottom: 0 }}
                                    size='medium'
                                    borderErrorOnly
                                  />
                                </Grid2>

                                <Grid2 {...{ xs: 1.5 }}>
                                  <Button
                                    variant='contained'
                                    sx={{
                                      bgcolor: '#2B3493!important',
                                      color: '#FFF!important',
                                      boxShadow: 'none!important',
                                    }}
                                    type='submit'
                                  >
                                    Post
                                  </Button>
                                </Grid2>
                              </Grid2>
                            </Stack>
                          </>
                        )}
                      </Stack>
                    </Grid2>

                    {/* ROW 4 */}
                    <Grid2 {...{ xs: 12 }}>
                      <Stack gap={3}>
                        <Badge
                          badgeContent={paymentType === 'others' ? 0 : others}
                          color='primary'
                        >
                          <Button
                            variant='contained'
                            size='large'
                            onClick={() => onClickPayment('others')}
                            sx={{ gap: 1 }}
                          >
                            Others{' '}
                            {totalOthersAmount > 0 && (
                              <span
                                style={{
                                  fontStyle: 'italic',
                                  fontWeight: 400,
                                  opacity: 0.6,
                                }}
                              >{`- Total Amount: ${totalOthersAmount}`}</span>
                            )}
                          </Button>
                        </Badge>
                        {paymentType === 'others' && (
                          <>
                            <Stack px={1.2}>
                              <Grid2 container spacing={2}>
                                {othersButtons.map((button, i) => (
                                  <CardItem
                                    key={button.title + i}
                                    button={button}
                                    badgeCount={
                                      othersItems?.[button.referenceID]
                                    }
                                    selectedCard={selectedCard}
                                    totalCard={othersButtons.length}
                                    type='others'
                                  />
                                ))}
                              </Grid2>
                            </Stack>

                            <Stack px={1.2}>
                              <Grid2 container spacing={2}>
                                {otherCard === 'Cheque' && (
                                  <>
                                    <Grid2
                                      {...{ xs: 12, sm: 4 }}
                                      sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                      }}
                                    >
                                      <AutoCompleteController
                                        control={control}
                                        errors={errors}
                                        name='issuingBank'
                                        label='Issuing Bank'
                                        options={optionsBank || []}
                                        borderErrorOnly
                                        disableClearable
                                        sx={{
                                          button: {
                                            boxShadow: 0,
                                            py: 0,
                                          },
                                          marginBottom: 0,
                                        }}
                                        textFieldProps={{ variant: 'standard' }}
                                      />
                                    </Grid2>
                                    <Grid2
                                      {...{ xs: 12, sm: 4 }}
                                      sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                      }}
                                    >
                                      <DatePickerController
                                        control={control}
                                        name='checkDate'
                                        label='Check Date'
                                        errors={errors}
                                        sx={{
                                          button: {
                                            boxShadow: 0,
                                            py: 0,
                                            color: 'unset',
                                            bgcolor: 'unset',
                                          },
                                          marginBottom: 0,
                                        }}
                                        slotProps={{
                                          textField: {
                                            variant: 'standard',
                                          },
                                        }}
                                      />
                                    </Grid2>
                                    <Grid2 {...{ xs: 12, sm: 4 }}>
                                      <InputFieldsController
                                        variant='standard'
                                        control={control}
                                        errors={errors}
                                        name='checkNo'
                                        label='Check #'
                                        wrapperStyle={{ marginBottom: 0 }}
                                        size='medium'
                                        borderErrorOnly
                                        InputProps={{
                                          inputProps: {
                                            maxLength: 16,
                                            textAlign: 'left',
                                          },
                                        }}
                                      />
                                    </Grid2>
                                  </>
                                )}
                                {/*  */}
                                {otherCard === 'GC' && (
                                  <Grid2 {...{ xs: 12 }}>
                                    <InputFieldsController
                                      variant='standard'
                                      control={control}
                                      errors={errors}
                                      name='giftCertificate'
                                      label='Gift Certificate #'
                                      wrapperStyle={{ marginBottom: 0 }}
                                      size='medium'
                                      borderErrorOnly
                                      InputProps={{
                                        // inputComponent: CustomNumberFormat,
                                        inputProps: {
                                          maxLength: 16,
                                          textAlign: 'left',
                                          // maxAmount: remainingCost,
                                        },
                                      }}
                                    />
                                  </Grid2>
                                )}
                                {/*  */}
                                <Grid2 {...{ xs: 12 }}>
                                  <InputFieldsController
                                    variant='standard'
                                    control={control}
                                    errors={errors}
                                    name='amount'
                                    label='Amount'
                                    wrapperStyle={{ marginBottom: 0 }}
                                    size='medium'
                                    borderErrorOnly
                                    InputProps={{
                                      inputComponent: CustomNumberFormat,
                                      inputProps: {
                                        maxLength: 16,
                                        textAlign: 'left',
                                        maxAmount: remainingCost,
                                        allowNegative: false,
                                      },
                                    }}
                                  />
                                </Grid2>
                                <Grid2
                                  {...{ xs: 10.5 }}
                                  sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                  }}
                                >
                                  <InputFieldsController
                                    variant='standard'
                                    control={control}
                                    errors={errors}
                                    name='remarks'
                                    label='Remarks'
                                    wrapperStyle={{ marginBottom: 0 }}
                                    size='medium'
                                    borderErrorOnly
                                  />
                                </Grid2>
                                <Grid2 {...{ xs: 1.5 }}>
                                  <Button
                                    variant='contained'
                                    sx={{
                                      bgcolor: '#2B3493!important',
                                      color: '#FFF!important',
                                      boxShadow: 'none!important',
                                    }}
                                    type='submit'
                                  >
                                    Post
                                  </Button>
                                </Grid2>
                              </Grid2>
                            </Stack>
                          </>
                        )}
                      </Stack>
                    </Grid2>
                  </>
                ) : (
                  <>
                    <Grid2 container direction='column' sx={{ height: '100%' }}>
                      <Grid2 container spacing={2}>
                        <Grid2 {...{ xs: 12 }}>
                          <Stack
                            direction={{ xs: 'column', md: 'row' }}
                            divider={
                              <Divider orientation='vertical' flexItem />
                            }
                            gap={3}
                            sx={{
                              alignItems: 'center',
                              justifyContent: 'center',
                              flex: 1,
                              pb: 3,
                            }}
                          >
                            <Stack sx={{ alignItems: 'center' }}>
                              <Typography
                                sx={{
                                  fontFamily: 'Montserrat',
                                  fontSize: '48px',
                                  fontWeight: 600,
                                  color: 'primary.main',
                                  letterSpacing: '2px',
                                }}
                              >
                                {formatNumber(totalCost, 2, 2)}
                              </Typography>
                              <Typography sx={{ color: '#637381' }}>
                                Total Paid
                              </Typography>
                            </Stack>

                            <Stack sx={{ alignItems: 'center' }}>
                              <Typography
                                sx={{
                                  fontFamily: 'Montserrat',
                                  fontSize: '48px',
                                  fontWeight: 600,
                                  color: 'primary.main',
                                  letterSpacing: '2px',
                                }}
                              >
                                {formatNumber(
                                  data?.parsedResponse?.change ?? '',
                                  2,
                                  2,
                                )}
                              </Typography>
                              <Typography sx={{ color: '#637381' }}>
                                Total Change
                              </Typography>
                            </Stack>
                          </Stack>
                        </Grid2>

                        <Grid2
                          {...{ xs: 8 }}
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                          }}
                        >
                          <InputFieldsController
                            variant='standard'
                            control={control}
                            errors={errors}
                            name='email'
                            label='Email'
                            placeholder='Enter Email'
                            wrapperStyle={{ marginBottom: 0 }}
                            size='medium'
                            borderErrorOnly
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position='start'>
                                  <MailOutline sx={{ fontSize: 23 }} />
                                </InputAdornment>
                              ),
                            }}
                          />
                        </Grid2>

                        <Grid2 {...{ xs: 4 }}>
                          <Button
                            variant='contained'
                            sx={{
                              bgcolor: '#2B3493!important',
                              color: '#FFF!important',
                              boxShadow: 'none!important',
                            }}
                            type='submit'
                          >
                            Send Receipt
                          </Button>
                        </Grid2>
                      </Grid2>

                      <Grid2 {...{ xs: 12 }} sx={{ mt: 'auto' }}>
                        <Button
                          variant='contained'
                          sx={{
                            bgcolor: '#2B3493!important',
                            color: '#FFF!important',
                            boxShadow: 'none!important',
                          }}
                          startIcon={<CheckOutlined />}
                          size='large'
                          onClick={handleNewSale}
                        >
                          New Transaction
                        </Button>
                      </Grid2>
                    </Grid2>
                  </>
                )}
              </Grid2>
            </Stack>
          </Stack>
        </Stack>
      </ModalWrapper>

      <POSSplitBillModal {...{ modalDisclosure: splitBillModal }} />
    </>
  );
};

export default POSBillOutModal;
